import type { DepositBonusTier } from '../types/wallet-management'

// What to draw for a tier: a currency top-up, a share of the deposit, or
// nothing. One function owns the decision so the chip and the label beside the
// amount input can never disagree about a tier.
export type BonusMarker =
  | { kind: 'flat'; bonus: number; bonusId?: number }
  | { kind: 'percent'; bonus: number; bonusId?: number }
  | null

// Only an explicit 'flat' or 'percent' draws. 'none' arrives with a non-zero
// `bonus` — a tier that exists but isn't advertised — and an unrecognised type
// is not guessed at, since either reading would put a wrong number in front of
// the player.
export const getBonusMarker = (tier?: DepositBonusTier): BonusMarker => {
  if (!tier || tier.bonus <= 0) return null
  const bonusId = tier.bonus_id
  if (tier.type === 'flat') {
    return { kind: 'flat', bonus: tier.bonus, bonusId }
  }
  if (tier.type === 'percent') {
    return { kind: 'percent', bonus: tier.bonus, bonusId }
  }
  return null
}

// What a marker is worth against a deposit, in currency: a flat tier is its own
// value, a percent tier a share of the amount. Left unrounded — the formatter
// trims to two decimals, and the backend is the authority on the credited
// figure.
export const getBonusValue = (marker: BonusMarker, amount: number): number => {
  if (!marker) return 0
  return marker.kind === 'flat' ? marker.bonus : (amount * marker.bonus) / 100
}

// The tier a deposit falls into: the highest tier at or below the amount, since
// the amount is free text and rarely lands exactly on a preset. A deposit of
// 499 therefore earns the 300 tier, and anything above the top tier earns the
// top one. Below the lowest tier there is no bonus at all.
//
// Reduced rather than indexed: `deposit_bonus_tiers` arrives in whatever order
// the backend sends, so this must not assume it is sorted.
export const findBonusTier = (
  tiers: DepositBonusTier[] | undefined,
  amount: number,
): DepositBonusTier | undefined => {
  if (!tiers?.length || !amount || amount < 0) return undefined
  return tiers.reduce<DepositBonusTier | undefined>((best, tier) => {
    if (tier.amount > amount) return best
    return !best || tier.amount > best.amount ? tier : best
  }, undefined)
}

// The marker for an amount, straight from the channel's tiers.
export const getAmountBonusMarker = (
  tiers: DepositBonusTier[] | undefined,
  amount: number,
): BonusMarker => getBonusMarker(findBonusTier(tiers, amount))

// Whether a channel offers any amount bonus at all. A tier list that is empty,
// all 'none', or all zeroes grants nothing, so there is no second source and no
// reason to offer the switch.
export const hasAnyBonusTier = (tiers?: DepositBonusTier[]): boolean =>
  (tiers ?? []).some(tier => getBonusMarker(tier) !== null)

// The preset amounts to offer: `fixed_amount` when the channel sends it, and
// the tiers' own amounts otherwise, so the chips survive a payload that carries
// only one of the two.
export const resolvePresetAmounts = (
  fixedAmount: number[] | undefined,
  tiers: DepositBonusTier[] | undefined,
): number[] =>
  fixedAmount?.length ? fixedAmount : (tiers ?? []).map(tier => tier.amount)
